import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    makeStyles,
    useTheme,
} from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApIconButton,
    ApTooltip,
} from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

const useStyles = makeStyles(_ =>
    createStyles({
        tooltipIcon: {
            width: '14px',
            height: '14px',
        },
        iconButton: {
            padding: '0',
            marginLeft: '-2px',
            minWidth: 'auto',
            height: 'auto',
            alignSelf: 'center',
        },
    }),
);

export interface TooltipProps {
    translationKey: string;
    placement?: 'top' | 'right' | 'bottom' | 'left' | 'top-start';
}

interface LabelWithTooltipProps {
    label: string;
    required?: boolean;
    tooltip?: TooltipProps;
    dataCy?: string;
}

export const LabelWithTooltip: React.FC<LabelWithTooltipProps> = ({
    label,
    required = false,
    tooltip,
    dataCy,
}) => {
    const theme = useTheme();
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();

    // If no tooltip provided, render label without tooltip icon
    if (!tooltip) {
        return (
            <UiText
                variant={FontVariantToken.fontSizeMBold}
                color={theme.palette.semantic.colorForegroundDeEmp}
                style={{
                    marginBottom: '4px',
                    fontSize: '12px',
                }}
            >
                {label}
                {required && ' *'}
            </UiText>
        );
    }

    // Defensive check: if tooltip doesn't have translationKey, render without tooltip
    if (!tooltip.translationKey) {
        return (
            <UiText
                variant={FontVariantToken.fontSizeMBold}
                color={theme.palette.semantic.colorForegroundDeEmp}
                style={{
                    marginBottom: '4px',
                    fontSize: '12px',
                }}
            >
                {label}
                {required && ' *'}
            </UiText>
        );
    }

    const tooltipText = translate({ id: tooltip.translationKey });

    return (
        <UiStack
            direction='row'
            align='center'
            mb={SpacingToken.Micro}
            data-cy={dataCy}
        >
            <UiText
                variant={FontVariantToken.fontSizeMBold}
                color={theme.palette.semantic.colorForegroundDeEmp}
            >
                {label}
                {required && ' *'}
            </UiText>
            <ApTooltip
                placement={tooltip.placement}
                content={tooltipText}
            >
                <ApIconButton
                    className={classes.iconButton}
                    data-cy={dataCy ? `${dataCy}-icon` : undefined}>
                    <InfoOutlinedIcon
                        className={classes.tooltipIcon}
                        color='action'
                        aria-label={tooltipText} />
                </ApIconButton>
            </ApTooltip>
        </UiStack>
    );
};
